import { useMemo, useState, type SyntheticEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { InvoiceTable } from '../components/InvoiceTable';
import { Async } from '../components/Async';
import { IcSearch } from '../components/icons';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import {
  getInvoices,
  getClientOptions,
  createInvoice,
  markInvoicePaid,
  chargeInvoice,
  createPayLink,
  addInvoiceAdjustment,
  refundInvoice,
  ApiError,
  type InvoiceRow,
  type CollectionMethod,
} from '../lib/api';
import { FLOW_META, INVOICE_STATUS, type FlowType, type InvoiceStatus } from '../lib/types';

const FLOWS: (FlowType | 'all')[] = ['all', 'platform', 'pharmacy', 'shipping', 'dr_network', 'manual'];
const STATUSES: (InvoiceStatus | 'all')[] = ['all', 'draft', 'open', 'past_due', 'partial', 'paid', 'failed', 'void'];

export const InvoicesPage = () => {
  const { can } = useAuth();
  const [sp] = useSearchParams();
  const initialStatus = sp.get('status');
  const [flow, setFlow] = useState<FlowType | 'all'>('all');
  const [status, setStatus] = useState<InvoiceStatus | 'all'>(
    initialStatus && (STATUSES as string[]).includes(initialStatus) ? (initialStatus as InvoiceStatus) : 'all',
  );
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);

  const state = useFetch(
    () => getInvoices({ flow: flow === 'all' ? undefined : flow, status: status === 'all' ? undefined : status }),
    [flow, status],
  );

  async function onCharge(inv: InvoiceRow) {
    setMsg(null);
    setBusyId(inv._id);
    try {
      const r = await chargeInvoice(inv._id);
      setMsg(`Charge ${r.status} — ${inv.clientName} (${inv.period.label}).`);
      state.reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? `Charge failed: ${err.message}` : 'Charge failed');
    } finally {
      setBusyId(null);
    }
  }

  async function onMarkPaid(inv: InvoiceRow) {
    const ref = window.prompt(
      `Mark ${inv.invoiceNumber ?? '#' + inv._id.slice(-6)} paid (${inv.collectionMethod.toUpperCase()}). Reference # (optional):`,
      '',
    );
    if (ref === null) return; // cancelled
    setMsg(null);
    setBusyId(inv._id);
    try {
      const r = await markInvoicePaid(inv._id, {
        method: inv.collectionMethod === 'ach' ? 'ach' : 'wire',
        reference: ref || undefined,
      });
      setMsg(`Marked ${inv.clientName} (${inv.period.label}) → ${r.status}.`);
      state.reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? `Failed: ${err.message}` : 'Failed');
    } finally {
      setBusyId(null);
    }
  }

  async function onAdjust(inv: InvoiceRow) {
    const desc = window.prompt(`Adjustment for ${inv.invoiceNumber ?? '#' + inv._id.slice(-6)} — description (e.g. "Goodwill credit", "Rush fee"):`, '');
    if (!desc) return;
    const raw = window.prompt(
      inv.amountPaidCents > 0
        ? `Partially paid — credits only, up to the $${(inv.amountDueCents / 100).toFixed(2)} balance. Amount in dollars (e.g. -50):`
        : 'Amount in dollars — negative for a credit (e.g. -50), positive for a fee (e.g. 25):',
      '',
    );
    if (raw === null) return;
    const dollars = parseFloat(raw);
    if (Number.isNaN(dollars) || dollars === 0) { setMsg('Enter a non-zero amount.'); return; }
    setMsg(null);
    setBusyId(inv._id);
    try {
      const r = await addInvoiceAdjustment(inv._id, { description: desc, amountCents: Math.round(dollars * 100) });
      setMsg(`Adjusted ${inv.clientName} → new total ${(r.totalCents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}.`);
      state.reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? `Adjust failed: ${err.message}` : 'Adjust failed');
    } finally {
      setBusyId(null);
    }
  }

  async function onRefund(inv: InvoiceRow) {
    const paidDollars = (inv.amountPaidCents / 100).toFixed(2);
    const raw = window.prompt(
      `Refund ${inv.invoiceNumber ?? '#' + inv._id.slice(-6)} — amount in dollars (max $${paidDollars}, blank = full refund):`,
      paidDollars,
    );
    if (raw === null) return; // cancelled
    const trimmed = raw.trim();
    let amountCents: number | undefined;
    if (trimmed !== '') {
      const dollars = parseFloat(trimmed);
      if (Number.isNaN(dollars) || dollars <= 0) { setMsg('Enter a positive amount.'); return; }
      amountCents = Math.round(dollars * 100);
      if (amountCents > inv.amountPaidCents) { setMsg('Refund exceeds amount paid.'); return; }
    }
    const reason = window.prompt('Reason (optional):', '') ?? undefined;
    setMsg(null);
    setBusyId(inv._id);
    try {
      const r = await refundInvoice(inv._id, { amountCents, reason: reason || undefined });
      setMsg(`Refunded ${inv.clientName} (${inv.period.label}) → ${r.status}.`);
      state.reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? `Refund failed: ${err.message}` : 'Refund failed');
    } finally {
      setBusyId(null);
    }
  }

  async function onPayLink(inv: InvoiceRow) {
    setMsg(null);
    try {
      const { token } = await createPayLink(inv._id);
      const url = `${window.location.origin}/pay/${token}`;
      try {
        await navigator.clipboard.writeText(url);
        setMsg(`Pay link copied: ${url}`);
      } catch {
        setMsg(`Pay link: ${url}`);
      }
    } catch (err) {
      setMsg(err instanceof ApiError ? `Could not create link: ${err.message}` : 'Could not create link');
    }
  }

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = state.data?.invoices ?? [];
    if (!needle) return list;
    return list.filter(
      (inv) =>
        inv.clientName.toLowerCase().includes(needle) ||
        inv._id.toLowerCase().includes(needle) ||
        (inv.invoiceNumber ?? '').toLowerCase().includes(needle),
    );
  }, [q, state.data]);

  return (
    <Page>
      <PageHeader
        title="Invoices"
        sub={state.data ? `${filtered.length} invoices` : undefined}
        actions={
          can('invoices:manage') ? (
            <button className="btn btn--primary" onClick={() => setBuilding((b) => !b)}>
              {building ? 'Cancel' : 'New invoice'}
            </button>
          ) : undefined
        }
      />

      {msg && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(59,130,246,.06)', fontSize: 13 }}>{msg}</div>
      )}

      {building && can('invoices:manage') && (
        <InvoiceBuilder
          onCancel={() => setBuilding(false)}
          onCreated={(label) => {
            setBuilding(false);
            setMsg(`Created invoice ${label}.`);
            state.reload();
          }}
        />
      )}

      <PCard style={{ padding: 14 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              border: '1px solid var(--p-border)',
              borderRadius: 6,
              background: '#fff',
              flex: 1,
              minWidth: 220,
            }}
          >
            <IcSearch size={14} style={{ color: 'var(--p-muted-fg)' }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by client or invoice #…"
              style={{ border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 13, flex: 1, background: 'transparent' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {FLOWS.map((f) => {
              const active = flow === f;
              return (
                <button
                  key={f}
                  onClick={() => setFlow(f)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 999,
                    border: '1px solid var(--p-border)',
                    background: active ? 'var(--p-primary)' : '#fff',
                    color: active ? '#fff' : 'var(--p-muted-fg)',
                    fontSize: 12,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {f === 'all' ? 'All' : FLOW_META[f].label}
                </button>
              );
            })}
          </div>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as InvoiceStatus | 'all')}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s === 'all' ? 'All statuses' : INVOICE_STATUS[s].label}</option>
            ))}
          </select>
        </div>
      </PCard>

      <Async state={state} isEmpty={(d) => d.invoices.length === 0} emptyLabel="No invoices yet.">
        {() => (
          <InvoiceTable
            invoices={filtered}
            backTo={{ to: '/invoices', label: 'All invoices' }}
            onCharge={can('payments:charge') ? onCharge : undefined}
            onMarkPaid={can('payments:charge') ? onMarkPaid : undefined}
            onPayLink={can('payments:charge') ? onPayLink : undefined}
            onAdjust={can('invoices:manage') ? onAdjust : undefined}
            onRefund={can('payments:charge') ? onRefund : undefined}
            busyId={busyId}
          />
        )}
      </Async>
    </Page>
  );
};

type Line = { description: string; amount: string };

function InvoiceBuilder({ onCreated, onCancel }: { onCreated: (label: string) => void; onCancel: () => void }) {
  const clients = useFetch(getClientOptions);
  const [clientId, setClientId] = useState('');
  const [method, setMethod] = useState<CollectionMethod>('wire');
  const [dueAt, setDueAt] = useState('');
  const [periodLabel, setPeriodLabel] = useState('');
  const [lines, setLines] = useState<Line[]>([{ description: '', amount: '' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);

  const submit = async (e: SyntheticEvent, draft = false) => {
    e.preventDefault();
    setError(null);
    const lineItems = lines
      .filter((l) => l.description.trim() && parseFloat(l.amount) > 0)
      .map((l) => ({ description: l.description.trim(), unitPriceCents: Math.round(parseFloat(l.amount) * 100) }));
    if (!clientId || lineItems.length === 0) {
      setError('Pick a client and add at least one line item with an amount.');
      return;
    }
    setBusy(true);
    try {
      const r = await createInvoice({
        clientId,
        lineItems,
        collectionMethod: method,
        dueAt: dueAt || undefined,
        periodLabel: periodLabel || undefined,
        draft,
      });
      onCreated(draft ? 'draft — review, then finalize to send' : r.invoice.invoiceNumber ?? 'created');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create invoice');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PCard style={{ padding: 16 }}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <Field label="Client">
            <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)} required>
              <option value="">Select client…</option>
              {(clients.data?.clients ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Collection">
            <select className="select" value={method} onChange={(e) => setMethod(e.target.value as CollectionMethod)}>
              <option value="wire">Wire</option>
              <option value="ach">ACH</option>
              <option value="auto_charge">Auto-charge (card)</option>
            </select>
          </Field>
          <Field label="Period label">
            <input className="select" value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} placeholder="optional" />
          </Field>
          <Field label="Due date">
            <input className="select" type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
          </Field>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, color: 'var(--p-muted-fg)' }}>Line items</div>
          {lines.map((l, i) => (
            <div key={i} style={{ display: 'flex', gap: 8 }}>
              <input
                className="select"
                style={{ flex: 1 }}
                placeholder="Description"
                value={l.description}
                onChange={(e) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
              />
              <input
                className="select"
                style={{ width: 130 }}
                type="number"
                step="0.01"
                min="0"
                placeholder="Amount ($)"
                value={l.amount}
                onChange={(e) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
              />
              <button
                type="button"
                className="btn"
                onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls))}
                title="Remove"
              >
                ✕
              </button>
            </div>
          ))}
          <div>
            <button type="button" className="btn" onClick={() => setLines((ls) => [...ls, { description: '', amount: '' }])}>
              + Add line
            </button>
          </div>
        </div>

        {error && <div style={{ color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create invoice'}
          </button>
          <button type="button" className="btn" onClick={(e) => submit(e, true)} disabled={busy}>
            Save as draft
          </button>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600 }}>
            Total: ${total.toFixed(2)}
          </span>
        </div>
      </form>
    </PCard>
  );
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--p-muted-fg)' }}>
    {label}
    {children}
  </label>
);
